import { getCargoOnboardParcelSchema } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { getCurrentUaeDate } from "@utils/date-time";
import {
  optionalStringMax,
  requiredBoolean,
  requiredNumberMax,
  requiredStringMax,
} from "@utils/schemas";
import type { TFunction } from "i18next";
import { z } from "zod";
import { FIELDS_LANG_BASE, FORM_FIELDS, type PreNominationRequestLabels } from "../constants";

export const getPreNominationRequestSchema = (t: TFunction, labels: PreNominationRequestLabels) => {
  const cargoOnboardParcelSchema = getCargoOnboardParcelSchema(t);

  const attachmentSchema = z
    .object({
      documentUrl: z.string().optional(),
      documentType: z.string().min(
        1,
        t("form-validation.mandatory", {
          label: t(`${FIELDS_LANG_BASE}.document-type`),
        }),
      ),
      otherSpecify: optionalStringMax(30, t(`${FIELDS_LANG_BASE}.other-specify`)),
      issueDate: z.union([z.date(), z.null()]),
      expirationDate: z.union([z.date(), z.null()]),
      documentName: requiredStringMax(50, t(`${FIELDS_LANG_BASE}.document-name`)),
      documentSummary: optionalStringMax(300, t(`${FIELDS_LANG_BASE}.document-summary`)),
    })
    .superRefine((attachment, ctx) => {
      if (attachment.documentType === "OTHERS_SPECIFY" && !attachment.otherSpecify.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherSpecify"],
          message: t("form-validation.mandatory", {
            label: t(`${FIELDS_LANG_BASE}.other-specify`),
          }),
        });
      }

      if (attachment.documentType === "CERTIFICATE" && !attachment.expirationDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["expirationDate"],
          message: t("form-validation.mandatory", {
            label: t(`${FIELDS_LANG_BASE}.expiration-date`),
          }),
        });
      }
    });

  return z.object({
    [FORM_FIELDS.VESSEL_ID]: z.string(),
    [FORM_FIELDS.ETA]: z.union([z.date(), z.null()]).superRefine((value, ctx) => {
      if (!value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label: labels.eta }),
        });
        return;
      }

      if (value.getTime() < getCurrentUaeDate().getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-past", { label: labels.eta }),
        });
      }
    }),
    [FORM_FIELDS.CALL_SIGN]: requiredStringMax(50, labels.callSign),
    [FORM_FIELDS.SUMMER_DWT]: requiredNumberMax(12, 3, labels.summerDwt),
    [FORM_FIELDS.ARRIVAL_DRAFT]: requiredNumberMax(8, 3, labels.arrivalDraft),
    [FORM_FIELDS.DEPARTURE_DRAFT]: requiredNumberMax(8, 3, labels.departureDraft),
    [FORM_FIELDS.HAS_HULL_PROTRUSION]: requiredBoolean(labels.hasHullProtrusion),
    [FORM_FIELDS.PARCELS]: z.array(cargoOnboardParcelSchema).min(
      1,
      t("form-validation.mandatory", {
        label: t("vessel-nomination.add.form.sections.type-and-quantity-of-cargo"),
      }),
    ),
    [FORM_FIELDS.CONFIRMATION]: z.boolean().refine((value) => value, {
      message: t("form-validation.mandatory", {
        label: labels.confirmation,
      }),
    }),
    [FORM_FIELDS.REMARKS]: optionalStringMax(500, labels.remarks),
    [FORM_FIELDS.ATTACHMENTS]: z.array(attachmentSchema),
  });
};
type PreNominationRequestSchema = ReturnType<typeof getPreNominationRequestSchema>;

export type PreNominationValidatedFormData = z.output<PreNominationRequestSchema>;
export type PreNominationFormData = z.infer<PreNominationRequestSchema>;
